'use client';

import { fetchSlugModel } from '@eliadmin/core/src/api/slugs/fetch-slug-model';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { addProductToFavorites } from '@eliadmin/core/src/api/customers/customers-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FiHeart } from 'react-icons/fi';
import { checkIfInLocalStorage } from '../utils/favorite-utils';
import { useState } from 'react';

type AddToFavoritesButtonProps = {
  productId: number;
  slug: string;
  className?: {
    container?: string;
    icon?: string;
  };
  isFavorite?: boolean;
  showText?: boolean;
};

// TODO: implement purely with server actions maybe
export function AddToFavoritesButton(props: AddToFavoritesButtonProps) {
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();
  const queryClient = useQueryClient();

  const { data: product } = useQuery({
    queryKey: [CacheKey.PRODUCT_DETAIL, props.slug, locale, me?.customer.id ?? null],
    queryFn: () =>
      fetchSlugModel({
        model: 'App\\Models\\Product',
        slug: props.slug,
        locale,
        authToken: authToken,
      }),
    enabled: isInitialized && !!authToken && !!props.slug,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const { mutate, isPending: isMutating } = useMutation({
    mutationFn: addProductToFavorites,
    onSettled: (data, error) => {
      queryClient.invalidateQueries({
        queryKey: [CacheKey.PRODUCT_DETAIL, props.slug, locale, me?.customer.id ?? null],
      });
      queryClient.invalidateQueries({ queryKey: [CacheKey.FAVORITE_PRODUCTS] });
      if (!error) {
        queryClient.setQueryData(
          [CacheKey.PRODUCT_DETAIL, props.slug, locale, me?.customer.id ?? null],
          {
            ...product,
            data: {
              ...product?.data,
              is_favorite: !isFavorite,
            },
          },
        );
      }
    },
  });

  const [isFavorite, setIsFavorite] = useState<boolean | undefined>(
    (checkIfInLocalStorage(props.productId) || product?.data.is_favorite || props.isFavorite) ??
      undefined,
  );

  function handleAddToFavorites(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    // if (!authToken) return router.push(ROUTES.SIGN_IN());
    if (!authToken) {
      const existingFavorites = JSON.parse(localStorage?.getItem('favoriteProducts') || '[]');

      if (existingFavorites.includes(props.productId)) {
        const updatedFavorites = existingFavorites.filter((id: number) => id !== props.productId);

        setIsFavorite(false);

        localStorage.setItem('favoriteProducts', JSON.stringify(updatedFavorites));
        window.dispatchEvent(new Event('storage'));
      } else {
        existingFavorites.push(props.productId);

        setIsFavorite(true);

        localStorage.setItem('favoriteProducts', JSON.stringify(existingFavorites));
        window.dispatchEvent(new Event('storage'));
      }

      return;
    }
    mutate({ productSlug: props.slug, authToken, locale });
  }

  return (
    <Button
      type="button"
      aria-label={isFavorite ? t('text532') : t('text531')}
      aria-pressed={!!isFavorite}
      aria-busy={isMutating}
      variant={'link'}
      onClick={e => handleAddToFavorites(e)}
      className={cn('w-full min-w-12 max-w-12 border border-[#EDEDED]', props.className?.container)}
    >
      {isFavorite === undefined || isMutating ? (
        <Spinner className="h-6 w-6" />
      ) : (
        <div className="flex flex-row">
          <FiHeart
            className={cn(
              'h-5 w-5 cursor-pointer transition-colors duration-200 ease-in-out',
              'text-destructive-foreground',
              isFavorite
                ? 'fill-destructive-foreground stroke-destructive-foreground'
                : 'fill-none',
              props.className?.icon,
            )}
            aria-hidden="true"
            strokeWidth={1.5}
          />
          {props.showText && (
            <span className="link ml-2 underline">{isFavorite ? t('text532') : t('text531')}</span>
          )}
        </div>
      )}
    </Button>
  );
}
